import { DbShare, UI_SHARE_KEY_STATUS } from '~src/types/db.d.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IntlShape } from 'react-intl';

export function getUiShareKeyStatus(share: DbShare): UI_SHARE_KEY_STATUS {
  if (share.share_key_state === 9) return UI_SHARE_KEY_STATUS.DELETED;
  if (share.share_key_state === 5) return UI_SHARE_KEY_STATUS.REJECTED;
  if (share.share_key_claimed_at > 0) return UI_SHARE_KEY_STATUS.CLAIMED;
  if (share.share_key_opened_at > 0) return UI_SHARE_KEY_STATUS.OPENED;
  if (share.share_key_first_invite_at > 0) return UI_SHARE_KEY_STATUS.INVITED;
  return UI_SHARE_KEY_STATUS.AWAITING;
}

export const CONTRACT_STATE_TEXT_MAP: Record<number, string> = {
  1: 'DRAFT',
  2: 'PARTIAL',
  3: 'AVAILABLE',
  4: 'ACCEPTED',
  5: 'REJECTED',
  6: 'COMPLETE',
  7: 'PROCESSED',
  8: 'VALIDATED',
  9: 'REMOVED',
  10: 'DELETED',
  11: 'EXPIRED',
};

export const getDBStateVisualIntl = (
  intl: IntlShape,
  state: number
): { color: string; title: string } => {
  switch (state) {
    case 1:
      return {
        color: 'orange',
        title: intl.formatMessage({
          id: 'db-state-draft',
          defaultMessage: 'Draft',
        }),
      };
    case 2:
      return {
        color: 'orange',
        title: intl.formatMessage({
          id: 'db-state-partial',
          defaultMessage: 'Partial',
        }),
      };
    case 3:
      return {
        color: 'green',
        title: intl.formatMessage({
          id: 'db-state-available',
          defaultMessage: 'Available',
        }),
      };
    case 4:
      return {
        color: 'green',
        title: intl.formatMessage({
          id: 'db-state-accepted',
          defaultMessage: 'Accepted',
        }),
      };
    case 5:
      return {
        color: 'red',
        title: intl.formatMessage({
          id: 'db-state-rejected',
          defaultMessage: 'Rejected',
        }),
      };
    case 6:
      return {
        color: 'green',
        title: intl.formatMessage({
          id: 'db-state-complete',
          defaultMessage: 'Complete',
        }),
      };
    case 7:
      return {
        color: 'purple',
        title: intl.formatMessage({
          id: 'db-state-processed',
          defaultMessage: 'Processed',
        }),
      };
    case 8:
      return {
        color: 'purple',
        title: intl.formatMessage({
          id: 'db-state-validated',
          defaultMessage: 'Validated',
        }),
      };
    case 9:
      return {
        color: 'purple',
        title: intl.formatMessage({
          id: 'db-state-removed',
          defaultMessage: 'Removed',
        }),
      };
    case 10:
      return {
        color: 'purple',
        title: intl.formatMessage({
          id: 'db-state-deleted',
          defaultMessage: 'Deleted',
        }),
      };
    case 11:
      return {
        color: 'black',
        title: intl.formatMessage({
          id: 'db-state-expired',
          defaultMessage: 'Expired',
        }),
      };
    default:
      return {
        color: 'gray',
        title: intl.formatMessage({
          id: 'db-state-unknown',
          defaultMessage: 'Unknown',
        }),
      };
  }
};

export function getContractVisual(
  state: number
): { color: string; title: string } {
  switch (state) {
    case 1:
      return { color: 'orange', title: 'DRAFT' };
    case 2:
      return { color: 'orange', title: 'PARTIAL' };
    case 3:
      return { color: 'green', title: 'AVAILABLE' };
    case 4:
      return { color: 'green', title: 'ACCEPTED' };
    case 5:
      return { color: 'red', title: 'REJECTED' };
    case 6:
      return { color: 'green', title: 'COMPLETE' };
    case 7:
      return { color: 'purple', title: 'PROCESSED' };
    case 8:
      return { color: 'purple', title: 'VALIDATED' };
    case 9:
      return { color: 'purple', title: 'REMOVED' };
    case 10:
      return { color: 'purple', title: 'DELETED' };
    case 11:
      return { color: 'black', title: 'EXPIRED' };
    default:
      return { color: 'grey', title: 'UNKNOWN' };
  }
}

export function getOfferStatusVisual(
  intl: IntlShape,
  offer: any
): { color: string; text: string } {
  // Value: NULL | 0 -> awaiting (grey)
  if (
    offer.state === null ||
    offer.state === 0 ||
    (offer.is_offer_created !== undefined && !offer.is_offer_created)
  ) {
    return {
      color: 'grey',
      text: intl.formatMessage({
        id: 'tender-vendor-column-contracts-awaiting',
      }),
    };
  }

  switch (offer.state) {
    // Value: 3 -> Reopened (orange)
    case 3:
      return {
        color: 'orange',
        text: intl.formatMessage({
          id: 'tender-vendor-column-contracts-reopened',
        }),
      };
    // Value: 4 / 6 -> Submitted (green)
    case 4:
    case 6:
      return {
        color: 'green',
        text: intl.formatMessage({
          id: 'tender-vendor-column-contracts-submitted',
        }),
      };
    // Value: 5 -> Rejected (red)
    case 5:
      return {
        color: 'red',
        text: intl.formatMessage({
          id: 'tender-vendor-column-contracts-rejected',
        }),
      };
    // Value: 1 -> In progress (orange)
    case 1:
    default:
      return {
        color: 'orange',
        text: intl.formatMessage({
          id: 'tender-vendor-column-contracts-in-progress',
        }),
      };
  }
}

export function getOfferStatusVisualIntl(
  intl: IntlShape,
  offer: any
): { color: string; text: string } {
  // Value: NULL | 0 -> awaiting (grey)
  if (
    offer.state === null ||
    offer.state === 0 ||
    (offer.is_offer_created !== undefined && !offer.is_offer_created)
  ) {
    return {
      color: 'grey',
      text: intl.formatMessage({
        id: 'state-awaiting',
        defaultMessage: 'Awaiting participation',
      }),
    };
  }

  switch (offer.state) {
    // Value: 3 -> Reopened (orange)
    case 3:
      return {
        color: 'orange',
        text: intl.formatMessage({
          id: 'offer-state-reopen',
          defaultMessage: 'Reopened',
        }),
      };
    // Value: 4 / 6 -> Submitted (green)
    case 4:
    case 6:
      return {
        color: 'green',
        text: intl.formatMessage({
          id: 'offer-state-delivered',
          defaultMessage: 'Delivered',
        }),
      };
    // Value: 5 -> Rejected (red)
    case 5:
      return {
        color: 'red',
        text: intl.formatMessage({
          id: 'offer-state-rejected',
          defaultMessage: 'Rejected',
        }),
      };
    // Value: 1 -> In progress (orange)
    case 1:
    default:
      return {
        color: 'orange',
        text: intl.formatMessage({
          id: 'offer-state-in-progress',
          defaultMessage: 'In progress',
        }),
      };
  }
}

export function getShareStatusVisual(share: DbShare) {
  if (share.share_key_state === 9) {
    return {
      icon: <FontAwesomeIcon icon={['far', 'trash']} />,
      color: 'purple',
      text: 'Deleted',
    };
  }
  if (share.share_key_state === 5) {
    return {
      icon: <FontAwesomeIcon icon={['far', 'ban']} />,
      color: 'red',
      text: 'Rejected',
    };
  }
  if (share.share_key_claimed_at > 0) {
    return {
      icon: <FontAwesomeIcon icon={['fad', 'check-circle']} />,
      color: 'green',
      text: 'Claimed',
    };
  }
  if (share.share_key_opened_at > 0) {
    return {
      icon: <FontAwesomeIcon icon={['far', 'envelope-open-text']} />,
      color: 'orange',
      text: 'Opened',
    };
  }
  if (share.share_key_first_invite_at > 0) {
    return {
      icon: <FontAwesomeIcon icon={['far', 'envelope']} />,
      color: 'orange',
      text: 'Invited',
    };
  }
  return {
    icon: <FontAwesomeIcon icon={['far', 'stopwatch']} />,
    color: 'grey',
    text: 'Awaiting',
  };
}

/**
 * Structural subset of DbShare that getShareStatusVisualIntl actually reads.
 * DbShare already satisfies this shape (no behavior change for existing
 * callers) - widened so SecondaryShareKeyVM (Primary-/Secondary Share Keys
 * feature) can reuse the same status pill/icon logic without needing an
 * unsafe cast.
 */
export interface ShareKeyStatusFields {
  share_key_state: number;
  share_key_claimed_at: number | null;
  share_key_opened_at: number | null;
  share_key_first_invite_at: number | null;
}

export function getShareStatusVisualIntl(
  intl: IntlShape,
  share: ShareKeyStatusFields
) {
  if (share.share_key_state === 9) {
    return {
      icon: <FontAwesomeIcon icon={['far', 'trash']} />,
      color: 'purple',
      text: intl.formatMessage({
        id: 'tender-share-key-state-deleted',
        defaultMessage: 'Deleted',
      }),
    };
  }
  if (share.share_key_state === 5) {
    return {
      icon: <FontAwesomeIcon icon={['far', 'ban']} />,
      color: 'red',
      text: intl.formatMessage({
        id: 'tender-share-key-state-rejected',
        defaultMessage: 'Rejected',
      }),
    };
  }
  if (share.share_key_claimed_at > 0) {
    return {
      icon: <FontAwesomeIcon icon={['fad', 'check-circle']} />,
      color: 'green',
      text: intl.formatMessage({
        id: 'tender-share-key-state-claimed',
        defaultMessage: 'Claimed',
      }),
    };
  }
  if (share.share_key_opened_at > 0) {
    return {
      icon: <FontAwesomeIcon icon={['far', 'envelope-open-text']} />,
      color: 'orange',
      text: intl.formatMessage({
        id: 'tender-share-key-state-opened',
        defaultMessage: 'Opened',
      }),
    };
  }
  if (share.share_key_first_invite_at > 0) {
    return {
      icon: <FontAwesomeIcon icon={['far', 'envelope']} />,
      color: 'orange',
      text: intl.formatMessage({
        id: 'tender-share-key-state-invited',
        defaultMessage: 'Invited',
      }),
    };
  }

  return {
    icon: <FontAwesomeIcon icon={['far', 'stopwatch']} />,
    color: 'grey',
    text: intl.formatMessage({
      id: 'tender-share-key-state-awaiting',
      defaultMessage: 'Awaiting',
    }),
  };
}

/**
 * Primary-/Secondary Share Keys: structural subset of DbShareKey needed to
 * mirror the backend's parent-inviter access check.
 */
export interface ShareKeyAccessFields {
  share_key_state?: number | null;
  parent_inviter?: { state: number } | null;
}

/**
 * Primary-/Secondary Share Keys: mirrors backend's exact access-check logic
 * (PM-specified pseudocode) for whether a share key can currently be opened.
 *
 * IF (key.parent_inviter == null || key.parent_inviter.state IN (3,4)) {
 *   // parent OK
 *   IF (key.state IN (9,10,11)) -> blocked (403)
 *   ELSE -> accessible (200)
 * } ELSE {
 *   // parent rejected(5) or otherwise revoked -> always blocked (403)
 * }
 */
export function isShareKeyAccessible(
  key?: ShareKeyAccessFields | null
): boolean {
  if (!key) return true;

  const parent = key.parent_inviter;
  const parentOk = !parent || parent.state === 3 || parent.state === 4;

  if (!parentOk) return false;

  const ownState = key.share_key_state;
  return typeof ownState !== 'number' || ownState < 9;
}
